/**
 * Structured data helper functions for JSON-LD schemas.
 *
 * These generate Organization, WebSite, and FAQ schemas that are
 * injected into the homepage via useStructuredData().
 */

import { pricingTiers } from '@/lib/pricingData';

export function getOrganizationSchema() {
  const offers = pricingTiers.map((tier) => ({
    '@type': 'Offer',
    name: tier.name === 'Unlimited' ? 'Unlimited Plan' : `${tier.name} Plan`,
    price: tier.price.replace('$', ''),
    priceCurrency: 'USD',
  }));

  const lowPrice = Math.min(...offers.map((o) => parseFloat(o.price))).toString();
  const highPrice = Math.max(...offers.map((o) => parseFloat(o.price))).toString();

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'JD Simplified',
    url: 'https://jdsimplified.com',
    logo: 'https://jdsimplified.com/images/JDSimplified_Logo.png',
    description:
      'AI-powered law school study platform offering instant legal answers, flashcards, and courses for students preparing for exams and the bar.',
    foundingDate: '2023',
    sameAs: ['https://www.youtube.com/@JDsimplified'],
    offers: {
      '@type': 'AggregateOffer',
      offerCount: offers.length.toString(),
      lowPrice,
      highPrice,
      priceCurrency: 'USD',
      offers,
    },
  };
}

export function getWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'JD Simplified',
    url: 'https://jdsimplified.com',
    description:
      'AI-powered platform for law students and bar takers. Connected notes, outlines, and study tools to help you learn faster and master the law.',
    publisher: {
      '@type': 'Organization',
      name: 'JD Simplified, LLC',
    },
  };
}

export function getFAQSchema() {
  const freeTier = pricingTiers.find((t) => t.name === 'Free');
  const unlimitedTier = pricingTiers.find((t) => t.name === 'Unlimited');

  const pricingAnswer = `JD Simplified offers a ${freeTier?.name.toLowerCase()} plan with limited features, and an ${unlimitedTier?.name} plan for ${unlimitedTier?.price}/${unlimitedTier?.period} that includes all AI chat, courses, flashcards, and study tools.`;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is JD Simplified?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'JD Simplified is an AI-powered platform for law students and bar takers that provides connected notes, outlines, case briefs, flashcards, and study tools to help you learn faster and master the law.',
        },
      },
      {
        '@type': 'Question',
        name: 'How much does JD Simplified cost?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: pricingAnswer,
        },
      },
      {
        '@type': 'Question',
        name: 'Is JD Simplified suitable for bar exam preparation?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! JD Simplified is designed to help with all aspects of law school including bar exam preparation, with features like AI-powered explanations, flashcards, case briefs, and targeted study materials.',
        },
      },
    ],
  };
}
